import { Button, Card, Dialog, Divider, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { FormSelectDynamic } from '../../../components/hook-form/form-select.dynamic.component';
import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../../hooks/useReactHookForm';
import { useSaveData } from '../../../hooks/useSaveData';
import { unitsApiKeyStore } from '../../../api/units-api-key-store';
import { projectsApiKeyStore } from '../../../api/projects-api-key-store';
import { Unit } from '../../../models/unit';
import { FormInput } from '../../../components/hook-form/form-input.component';
import { FormFormattedInput } from '../../../components/hook-form/form-formatted-input.component';
import { ZIPCODE_FORMAT } from '../../../constants';
import { ContactsForm } from './contacts.form';
import { NotesDialog } from '../../note/notes.dialog';
import DeleteIcon from '@mui/icons-material/Delete';
import { useCallback, useEffect, useRef, useState } from 'react';
import { divisionApiKeyStore } from '../../../api/masters/division-api-key-store';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import { NavLink, useNavigate } from 'react-router-dom';
import { Icons } from '../../../assets/icons';
import { useFileDownload } from '../../../components/file-download';
import { useDeleteData } from '../../../hooks/useDeleteData';
import { FormSelect } from '../../../components/hook-form/form-select.component';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { ApiErrorComponent } from '../../../components/api-error.component';
import { Dialog as KendoDialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { FormAutoCompleteSelectDynamic } from '../../../components/hook-form/form-autocomplete-select.dynamic.component';
import { stateApiKeyStore } from '../../../api/states-api-key-store';
import { countyApiKeyStore } from '../../../api/county-api-key-store';
import { ServicesForm } from './services-bundle-tab/services.form';
import { rebateCompanyApiKeyStore } from '../../../api/masters/rebate-company-api-key-store';
import { SelectFieldOption } from '../../../components/hook-form/hook-form-error';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { UnitRebate } from './unit-rebate.component';
import * as Yup from 'yup';
import { DateFormat, FormDateField } from '../../../components/hook-form/form-date.field.component';
import { ProctorResultComponent } from './proctor-result/proctor-result.component';
import { FormAutoCompleteMultiSelectDynamic } from '../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { GuardedComponent } from '../../../components/guarded-component';
import { UserPermissionName } from '../../../models/user-permission-name';
import { UnitDocumentDialog } from './unit-document/unit-document.dialog';
import { FormCheckbox } from '../../../components/hook-form/form-checkbox.component';
import { useAuthorize } from '../../../hooks/use-authorize';
import { parseErrorMessageRecursive } from '../../../utils/yup-error-parser.util';
import { useSnackbar } from '../../../hooks/use-snackbar.hook';
import { departmentsApiKeyStore } from '../../../api/masters/department-api-key-store';
import { getAddressCoreAndType } from '../../../utils/abbreviations.util';

const styling = {
  labelStyle: { color: '#0000b5', fontSize: '14px' },
  buttonStyle: { width: '100px' },
  buttonSave: { width: 'auto', marginLeft: '8px', padding: '7px 16px 8px' },
  dropZone: {
    padding: '10px 5px 10px',
    border: '0.1em dashed #99A3A4',
    backgroundColor: '#F2F3F4',
    // display: 'none',
    justifyContent: 'center',
    cursor: 'pointer',
    borderRadius: '4px',
    marginRight: '0px',
  },
};

const options = [
  {
    value: 'Cooling',
    label: 'Cooling',
    isDefault: false,
  },
  {
    value: 'Heating',
    label: 'Heating',
    isDefault: false,
  },
];

const ffServiceCat: SelectFieldOption[] = [
  { value: 'Third-Party Only', label: 'Third-Party Only', isDefault: false },
  { value: 'Third-Party and PPI', label: 'Third-Party and PPI', isDefault: false },
  { value: 'PPI Only', label: 'PPI Only', isDefault: false },
];
interface UnitDetailsFormProps {
  unit: Unit;
  refetchUnitData: () => void;
}

const unitValidationSchema = Yup.object().shape({
  zip: Yup.string()
    .matches(/(^$|^\d{5}$|^\d{9}$)/, { message: 'Invalid zip code' })
    .nullable(),
  unitName: Yup.string().required('Lot/Unit is required').max(9, 'Lot/Unit cannot exceed 9 characters'),
  address: Yup.string().required('Address is required').max(31, 'Address cannot exceed 31 characters'),
  departments: Yup.object()
    .shape({
      departmentID: Yup.string()
        .nullable()
        .required('Department is required')
        .test('not-zero', 'Department is required', (value) => value !== '' && value !== '0'),
    })
    .required('Department is required'),
});

const isDUXEnabled = process.env.REACT_APP_DUX_FLAG === 'true';

const normalize = (text: string) => text?.toLowerCase()?.trim()?.replace(/\s+/g, '');

export const UnitDetailsForm = ({ unit, refetchUnitData }: UnitDetailsFormProps) => {
  // const isUATEnable = process.env.REACT_APP_UAT_FLAG === 'true';
  const [openNoteDialog, setOpenNoteDialog] = useState(false);
  const [openFileDialog, setOpenFileDialog] = useState(false);
  const [unitContactList, setUnitContactList] = useState<any[]>([]);
  const { showSnackbar } = useSnackbar();
  const [file, setFile] = useState<any>();
  const [UnitHVACFileName, setUnitHVACFileName] = useState<any>(unit?.hvacDesignFileName);
  const [unitAccaReportFileName, setUnitAccaReportFileName] = useState<any>(unit?.accaReportFileName);
  const navigate = useNavigate();
  const form = useReactHookForm({
    yupSchema: unitValidationSchema,
    defaultValues: {
      ...unit,
      state: { stateID: unit?.state ? unit?.state : 0, stateName: '' },
      county: { countyID: unit?.county ? unit?.county : 0, countyName: '' },
      departments: Number(unit?.departmentID)
        ? {
            departmentID: unit?.departmentID ?? '',
            departmentName: unit?.departmentName ?? '',
          }
        : null,
      rebates: unit?.rebates ? unit?.rebates : '',
      rebateCompanies: {
        utilRebateCompanyID: unit?.rebateCompany ?? '',
        utilRebateCompanyName: '',
      },
    },
  });
  const initialJobSite = useRef(form.getValues('jobsite')); // Store the initial job number in a ref

  const handleUnitNameChange = (e: any) => {
    const newJobsite = `${initialJobSite.current}${e}`.trim();
    form.setValue('jobsite', newJobsite);
  };
  const handleJobnameNameChange = (e: any) => {
    initialJobSite.current = e;
  };
  const [openDeleteDialog, setDeleteDialog] = useState<boolean>(false);
  const [openAccDeleteDialog, setAccDeleteDialog] = useState<boolean>(false);
  const { isLoading, mutateAsync } = useSaveData<any>(unitsApiKeyStore.updateUnitInfo());
  const [projectLotData, setProjectLotData] = useState<any>();

  const {
    data,
    error,
    isError,
    isLoading: fileDeleteLoading,
    mutateAsync: mutateDeleteFile,
  } = useDeleteData(unitsApiKeyStore.deleteUnitFile(unit?.unitID));
  const { downloadFile, isLoading: isFileDownloadLoading } = useFileDownload();

  const {
    error: accReportError,
    isError: isAccReportError,
    isLoading: accReportDeleteLoading,
    mutateAsync: mutateAccDeleteReport,
  } = useDeleteData(unitsApiKeyStore.deleteUnitAccReportFile(unit?.unitID));

  const { data: projectData, isLoading: isProjectLoading } = useLoadData<ApiResponse<any>>(
    projectsApiKeyStore.getProjectDetails(Number(unit?.projectID ?? 0))
  );

  const {
    isLoading: isLoadingLotInfo,
    isFetching: isFetchingLotInfo,
    data: lotInfoData,
    refetch: refetchLotInfo,
  } = useLoadData<ApiResponse<any>>(projectsApiKeyStore.getProjectLotInfo(Number(unit?.projectID ?? 0)));

  useEffect(() => {
    if (lotInfoData?.data?.singleOrMultiLots?.length > 0) {
      const updatedData = lotInfoData?.data?.singleOrMultiLots
        ?.filter((lot: any) => lot?.unitID !== unit?.unitID)
        ?.map((lot: any) => ({
          ...lot,
          stateID: !lot?.stateID ? null : { stateID: lot?.stateID },
          municipalityID: !lot?.municipalityID ? null : { municipalityID: lot?.municipalityID },
          // countyID: { countyID: lot?.countyID },
        }));

      setProjectLotData(updatedData);
    }
  }, [lotInfoData?.data]);

  console.log('projectLotData', projectLotData);

  const handleOpenNoteDialogClick = () => {
    setOpenNoteDialog(true);
  };

  const handleOpenFileDialogClick = () => {
    setOpenFileDialog(true);
  };

  const handleDrop = useCallback((acceptedFiles: any) => {
    setFile(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
  });

  const handleCloseNoteDialog = () => {
    setOpenNoteDialog(false);
  };
  // const handleSubmit = async (values: any) => {
  //   mutateAsync({
  //     data: { ...values, unitContacts: values.unitContacts?.map((p: any) => p.contactsID) },
  //   });
  // };

  const handleDeleteFileClick = async () => {
    if (unit?.hvacDesignFileName) {
      const deleteData = await mutateDeleteFile();
      refetchUnitData();
    }
    setFile(undefined);
    setUnitHVACFileName('');
    form.setValue('hvacDesignFileName', '');
    form.setValue('hvacDesignFileType', '');
    form.setValue('hvacDesignFile', '');
    handleClosedDeleteDialog();
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialog(true);
  };

  const handleClosedDeleteDialog = () => {
    setDeleteDialog(false);
  };

  const handleDownloadFile = async (data: any) => {
    const value = { unitID: unit?.unitID, fileName: data?.hvacDesignFileName, fileType: data?.hvacDesignFileType };
    downloadFile({ additionalData: value });
  };

  const handleSubmit = async (value: any): Promise<any> => {
    // const contacts = value?.unitContacts?.map((p: any) => p.contactsID);
    const unitDivisions = value?.unitDivisions?.map((p: any) => p.divisionID);

    const formData = new FormData();

    //isEnergyStart
    formData.append('Airflow', value.airflow);
    formData.append('HVACDesignFile', file ?? '');
    formData.append('HVACDesignFileName', file ? file?.name : value?.hvacDesignFileName);
    formData.append('HVACDesignFileType', file ? file?.type : value?.hvacDesignFileType);
    formData.append('HVACDesignFileURL', value?.hvacDesignFile ?? '');
    formData.append('HVACContractor', value?.hvacContractor ?? '');

    // formData.append('Sys1AirFlowResult', value?.sys1AirFlowResult ?? '');
    // formData.append('Sys1AirFlowScoring', value?.sys1AirFlowScoring ?? '');
    // formData.append('Sys1AirFlowGrading', value?.sys1AirFlowGrading ?? '');
    // formData.append('Sys2AirFlowResult', value?.sys2AirFlowResult ?? '');
    // formData.append('Sys2AirFlowScoring', value?.sys2AirFlowScoring ?? '');
    // formData.append('Sys2AirFlowGrading', value?.sys2AirFlowGrading ?? '');

    formData.append(
      'BillingAgainst',
      !value?.billingAgainst || value?.billingAgainst === 'null' ? '' : value?.billingAgainst
    );
    formData.append('UnitType', !value?.unitType || value?.unitType === 'null' ? '' : value?.unitType);

    formData.append('UnitID', value?.unitID);

    //isHVACGrading
    formData.append('ACCAReportFile', accFile ?? '');
    formData.append('ACCAReportFileName', accFile ? accFile?.name : value?.accaReportFileName);
    formData.append('ACCAReportFileType', accFile ? accFile?.type : value?.accaReportFileType);
    formData.append('ACCAReportFileURL', value?.accaReportFile ?? '');

    formData.append('DesignedAHModel', value?.designedAHModel ?? '');
    formData.append('BlowerFanAirflowDesign', value?.blowerFanAirflowDesign ?? '');
    formData.append('BlowerFanAirflowDesignSetting', value?.blowerFanAirflowDesignSetting ?? '');

    formData.append('WeighInDataReceived', value?.weighInDataReceived ?? false);

    formData.append('Sys1DesignedAHModel', value?.sys1DesignedAHModel ?? '');
    formData.append('Sys1BlowerFanAirflowDesign', value?.sys1BlowerFanAirflowDesign ?? '');
    formData.append('Sys1BlowerFanAirflowDesignSetting', value?.sys1BlowerFanAirflowDesignSetting ?? '');

    unitContactList?.length > 0
      ? unitContactList?.forEach((c: any) => {
          const item = {
            contactsID: c?.contactsID,
            customerID: value?.customerID ?? 0,
            officeID: value?.officeID ?? 0,
            projectID: value?.projectID ?? 0,
            unitID: value?.unitID ?? 0,
            isFastFieldCM: c?.isFastFieldCM ?? false,
            contactsNotificationIDs:
              c?.contactsNotificationIDs?.length > 0
                ? c?.contactsNotificationIDs?.map((item: any) => item.notificationReportStatusID)
                : [],
            ppiReportStatusIDs:
              c?.ppiReportStatusIDs?.length > 0
                ? c?.ppiReportStatusIDs?.map((item: any) => item.notificationReportStatusID)
                : [],
            susReportStatusIDs:
              c?.susReportStatusIDs?.length > 0
                ? c?.susReportStatusIDs?.map((item: any) => item.notificationReportStatusID)
                : [],
            otherReportStatusIDs:
              c?.otherReportStatusIDs?.length > 0
                ? c?.otherReportStatusIDs?.map((item: any) => item.notificationReportStatusID)
                : [],
          };
          formData.append('UnitContacts', JSON.stringify(item));
        })
      : value?.unitContacts?.forEach((c: any) => {
          const item = {
            contactsID: c?.contactsID,
            customerID: value?.customerID ?? 0,
            officeID: value?.officeID ?? 0,
            projectID: value?.projectID ?? 0,
            unitID: value?.unitID ?? 0,
            isFastFieldCM: c?.isFastFieldCM ?? false,
            contactsNotificationIDs:
              c?.contactsNotificationIDs?.length > 0
                ? c?.contactsNotificationIDs?.map((item: any) => item.notificationReportStatusID)
                : [],
            ppiReportStatusIDs:
              c?.ppiReportStatusIDs?.length > 0
                ? c?.ppiReportStatusIDs?.map((item: any) => item.notificationReportStatusID)
                : [],
            susReportStatusIDs:
              c?.susReportStatusIDs?.length > 0
                ? c?.susReportStatusIDs?.map((item: any) => item.notificationReportStatusID)
                : [],
            otherReportStatusIDs:
              c?.otherReportStatusIDs?.length > 0
                ? c?.otherReportStatusIDs?.map((item: any) => item.notificationReportStatusID)
                : [],
          };
          formData.append('UnitContacts', JSON.stringify(item));
        });

    // Array.isArray(contacts) &&
    //   contacts?.forEach((value: any) => {
    //     formData.append('UnitContacts', value);
    //   });

    // if (Array.isArray(contacts)) {
    //   formData.append('UnitContacts', JSON.stringify(contacts)); // Sends array as JSON string
    // }

    Array.isArray(unitDivisions) &&
      unitDivisions?.forEach((value: any) => {
        formData.append('UnitDivisions', value);
      });
    formData.append('MunicipalityID', value?.municipalityID);
    formData.append('MarketAreaID', value?.marketAreaID);
    // formData.append('DivisionID', value?.divisionID);
    formData.append('Address', value.address ?? '');
    formData.append('UnitName', value.unitName ?? '');
    formData.append('JobNumber', value?.jobNumber ?? '');
    formData.append('Jobsite', value?.jobsite ?? '');
    formData.append('FeeOwner', value?.feeOwner ?? '');

    formData.append('Rebates', value?.rebates ?? '');
    formData.append('RebateCompany', value?.rebateCompanies?.utilRebateCompanyID ?? '');

    formData.append('BuildingPermit', value?.buildingPermit ?? '');
    formData.append('MechanicalPermit', value?.mechanicalPermit ?? '');
    formData.append('ElectricalPermit', value?.electricalPermit ?? '');
    formData.append('PlumbingPermit', value?.plumbingPermit ?? '');
    formData.append('RoofPermit', value?.roofPermit ?? '');
    formData.append('gasPermit', value?.gasPermit ?? '');
    formData.append('lowVoltagePermit', value?.lowVoltagePermit ?? '');

    formData.append('City', value?.city ?? '');
    formData.append('State', value?.state?.stateID ?? '');
    formData.append('Zip', value?.zip ?? '');
    formData.append('County', value?.county?.countyID ?? '');

    formData.append('IrrigationPermit', value?.irrigationPermit ?? '');
    formData.append('DrivewayPermit', value?.drivewayPermit ?? '');
    formData.append('SquareFeet', value.squareFeet);
    formData.append('AchArea', value.achArea);
    formData.append('WindowArea', value.windowArea);
    formData.append('Volume', value.volume);
    formData.append('RegistrationNumber', value.registrationNumber ?? '');
    formData.append('HersScore', value.hersScore);

    formData.append('ApplicationNumber', value?.applicationNumber ?? '');
    formData.append('ParcelID', value?.parcelID ?? '');
    formData.append('AlternateID', value?.alternateID ?? '');
    formData.append('PermitDate', value?.permitDate !== 'Invalid Date' && !!value?.permitDate ? value?.permitDate : '');
    formData.append(
      'closingDate',
      value?.closingDate !== 'Invalid Date' && !!value?.closingDate ? value?.closingDate : ''
    );
    formData.append('FFServiceCat', value?.ffServiceCat);
    formData.append('syncWithDux', value?.syncWithDux);
    formData.append('includeHVACGrading', value?.includeHVACGrading);
    formData.append('energyStar', value?.energyStar);

    formData.append('DepartmentID', value?.departments?.departmentID ?? 0);

    await mutateAsync({ data: formData }).then((data) => navigate(`/units/profile/${data?.data?.unitID}`));
    // refetchUnitData?.();
    // form.reset({ fileUpload: [], description: '' });
  };

  const handleError = (error: any) => {
    const messages = parseErrorMessageRecursive(error);

    let requiredMessages: any[] = [];
    let otherMessages: any[] = [];

    // Classify messages
    Array.isArray(messages) &&
      messages?.forEach((message) => {
        if (message.endsWith('is required')) {
          requiredMessages.push(message?.split(' is required')[0]?.trim());
        } else {
          otherMessages.push(message.trim());
        }
      });

    // Remove duplicates
    requiredMessages = Array.from(new Set(requiredMessages));
    otherMessages = Array.from(new Set(otherMessages));

    // Construct the final message
    const finalRequiredMessage = requiredMessages.length > 0 ? `${requiredMessages?.join(', ')} is required` : '';
    const finalOtherMessage = otherMessages?.join(', ');

    // Combine messages
    const finalMessage = [finalRequiredMessage, finalOtherMessage]?.filter(Boolean)?.join(', ');

    // Show the final formatted message
    showSnackbar(`Validation Error: ${finalMessage}`, 'error');
  };

  const handleUnitContactList = (unitContacts: any[]) => {
    setUnitContactList(unitContacts ?? []);
  };

  const isSyncWithDuxPermission = useAuthorize([UserPermissionName?.SYNC_WITH_DUX]);

  const handleFormSubmit = (e: any) => {
    const { errors } = form.formState;
    if (errors.unitName || errors.address) {
      handleError(errors);
      return;
    }
    form.handleSubmit(handleSubmit as any, handleError)(e);
  };

  const handleAddressChange = (value: string) => {
    setAddressAbbreviationWarning('');

    if (!value || value.trim() === '' || !projectLotData?.length) return;

    // Exact match — API duplicate check will handle it, no abbreviation warning needed
    const isExactMatch = projectLotData.some((lot: any) => normalize(lot?.address ?? '') === normalize(value));
    if (isExactMatch) return;

    const { core: currentCore, streetType: currentStreetType } = getAddressCoreAndType(value);

    if (currentStreetType !== null && currentCore !== '') {
      const isAbbreviationMatch = projectLotData.some((lot: any) => {
        const { core: otherCore, streetType: otherStreetType } = getAddressCoreAndType(lot?.address ?? '');
        return otherCore === currentCore && otherStreetType === currentStreetType;
      });

      if (isAbbreviationMatch) {
        setAddressAbbreviationWarning('Address matches an existing address after abbreviation standardization.');
      }
    }
  };

  const [addressAbbreviationWarning, setAddressAbbreviationWarning] = useState('');

  const [isEnergyStart, setEnergyStarCheckbox] = useState<boolean>(unit?.energyStar ?? false);
  const handleEnergyStarCheckbox = (event: any) => {
    setEnergyStarCheckbox(event?.target?.checked);
    // setFile(undefined);
    // setUnitHVACFileName("");
    // form.setValue('airflow', "");
    // form.setValue('hvacDesignFileName', "");
    // form.setValue('hvacDesignFileType', "");
    // form.setValue('hvacContractor', "");
    // form.setValue('hvacDesignFile', "");
  };

  const [accFile, setAccFile] = useState<any>();

  const handleAccDrop = useCallback((acceptedFiles: any) => {
    setAccFile(acceptedFiles[0]);
  }, []);

  const handleDownloadAccFile = async (data: any) => {
    const value = {
      unitID: unit?.unitID,
      fileName: data?.accaReportFileName,
      fileType: data?.accaReportFileType,
      fileSection: 'accafile',
    };
    downloadFile({ additionalData: value });
  };

  const handleAccDeleteFileClick = async () => {
    if (unit?.accaReportFileName) {
      const deleteData = await mutateAccDeleteReport();
      refetchUnitData();
    }
    setAccFile(undefined);
    setUnitAccaReportFileName('');
    form.setValue('accaReportFileName', '');
    form.setValue('accaReportFileType', '');
    form.setValue('accaReportFile', '');
    handleClosedAccDeleteDialog();
  };

  const handleOpenAccDeleteDialog = () => {
    setAccDeleteDialog(true);
  };

  const handleClosedAccDeleteDialog = () => {
    setAccDeleteDialog(false);
  };

  const {
    getRootProps: getAccRootProps,
    getInputProps: getAccInputProps,
    isDragActive: isAccDragActive,
  } = useDropzone({
    onDrop: handleAccDrop,
  });

  const [isHVACGrading, setHVACGradingCheckbox] = useState<boolean>(unit?.includeHVACGrading ?? false);
  const handleHVACGradingCheckbox = (event: any) => {
    setHVACGradingCheckbox(event?.target?.checked);
    // setAccFile(undefined);
    // setUnitAccaReportFileName("");
    // form.setValue('designedAHModel', "");
    // form.setValue('accaReportFileName', "");
    // form.setValue('accaReportFileType', "");
    // form.setValue('blowerFanAirflowDesign', "");
    // form.setValue('blowerFanAirflowDesignSetting', "");
    // form.setValue('accaReportFile', "");

    // form.setValue('sys1AirFlowResult', "");
    // form.setValue('sys1AirFlowScoring', "");
    // form.setValue('sys1AirFlowGrading', "");
    // form.setValue('sys2AirFlowResult', "");
    // form.setValue('sys2AirFlowScoring', "");
    // form.setValue('sys2AirFlowGrading', "");

    // form.setValue('sys1DesignedAHModel', "");
    // form.setValue('sys1BlowerFanAirflowDesign', "");
    // form.setValue('sys1BlowerFanAirflowDesignSetting', "");
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <>
      <ReactHookForm formName="UNIT_FORM" onSubmit={() => {}} formContextProps={form} isBusy={isLoading}>
        <Grid container justifyContent="space-between" spacing={0}>
          <Grid item lg={12} sm={12} className="normalPad">
            <Grid container spacing={1} className="normalPad">
              <Grid item lg={3}>
                <FormInput label="Customer" name="customerName" disabled />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Office" name="officeName" disabled />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Project" name="projectName" disabled />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Project Code" name="projectCode" disabled />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Project Type" name="projectType" disabled />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Building Name" name="buildingName" disabled />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Community Type" name="communityType" disabled />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Floor" name="floor" disabled />
              </Grid>
              {/* </Grid>
            <Grid container spacing={1} className="normalPad"> */}
              <Grid item lg={3}>
                <FormInput
                  label="Jobsite"
                  // disabled
                  name="jobsite"
                  onChange={(e: any) => handleJobnameNameChange(e)}
                  validationQueryRoute={(value: string) =>
                    projectsApiKeyStore.checkDuplicateLotAddressJobsite(
                      Number(unit?.projectID ?? 0),
                      '',
                      Number(unit?.unitID ?? 0),
                      '',
                      value
                    )
                  }
                />
              </Grid>
              <Grid item lg={3}>
                <FormInput
                  label="Lot/Unit#"
                  name="unitName"
                  isCustomError={false}
                  onChange={(e: any) => handleUnitNameChange(e)}
                  validationQueryRoute={(value: string) =>
                    projectsApiKeyStore.checkDuplicateLotAddress(
                      Number(unit?.projectID ?? 0),
                      '',
                      Number(unit?.unitID ?? 0),
                      value,
                      ''
                    )
                  }
                />
              </Grid>
              <Grid item lg={3}>
                <FormInput
                  label="Address"
                  name="address"
                  isCustomError={false}
                  onChange={(value: string) => handleAddressChange(value)}
                  validationQueryRoute={(value: string) =>
                    projectsApiKeyStore.checkDuplicateLotAddress(
                      Number(unit?.projectID ?? 0),
                      value,
                      Number(unit?.unitID ?? 0),
                      '',
                      ''
                    )
                  }
                />
                {addressAbbreviationWarning && !form.formState.errors.address && (
                  <Typography variant="caption" style={{ color: '#f57c00' }}>
                    {addressAbbreviationWarning}
                  </Typography>
                )}
              </Grid>

              <Grid item lg={3}>
                <FormInput label="City" name="city" />
              </Grid>
              <Grid item lg={3}>
                <FormAutoCompleteSelectDynamic
                  name="state"
                  label="State"
                  getQueryKeyConfig={() => stateApiKeyStore.getStates()}
                  optionsValueKey="stateID"
                  optionsLabelKey="stateName"
                />
              </Grid>
              <Grid item lg={3}>
                <FormFormattedInput label="Zip Code" name="zip" format={ZIPCODE_FORMAT} />
              </Grid>
              <Grid item lg={3}>
                <FormAutoCompleteSelectDynamic
                  name="county"
                  label="County"
                  getQueryKeyConfig={() => countyApiKeyStore.getCounties()}
                  optionsValueKey="countyID"
                  optionsLabelKey="countyName"
                />
              </Grid>
              <Grid item lg={3}>
                <FormSelectDynamic
                  name="marketAreaID"
                  label="Market Area"
                  optionsValueKey="marketAreaID"
                  optionsLabelKeys={['marketAreaName']}
                  getQueryKeyConfig={() => projectsApiKeyStore.getProjectMarketAreas(unit.projectID)}
                />
              </Grid>
              <Grid item lg={3}>
                <FormSelectDynamic
                  name="municipalityID"
                  label="Municipality"
                  optionsValueKey="municipalityID"
                  optionsLabelKeys={['muncipalityName']}
                  getQueryKeyConfig={() => projectsApiKeyStore.getProjectMunicipalities(unit.projectID)}
                />
              </Grid>

              <Grid item lg={3}>
                <FormAutoCompleteMultiSelectDynamic
                  name="unitDivisions"
                  label="Divisions"
                  getQueryKeyConfig={() => divisionApiKeyStore.getProjectDivisions(unit.projectID)}
                  optionsLabelKey="divisionName"
                  optionsValueKey="divisionID"
                  disableCloseOnSelect={true}
                />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Fee Owner" name="feeOwner" />
              </Grid>
              {unit?.showUnitType && (
                <Grid item lg={3}>
                  <FormSelect
                    label="Unit Type"
                    name="unitType"
                    options={[
                      { value: 'onestory', label: '1-Story' },
                      { value: 'twostory', label: '2-Story' },
                    ]}
                  />
                </Grid>
              )}
              <Grid item lg={3}>
                <FormSelect name="ffServiceCat" label="FastField Service Cat" options={ffServiceCat} />
              </Grid>
              <Grid item lg={3}>
                <FormInput
                  label="Builder Job Number"
                  name="jobNumber"
                  validationQueryRoute={(value: string) =>
                    projectsApiKeyStore.checkDuplicateLotAddress(
                      Number(unit?.projectID ?? 0),
                      '',
                      Number(unit?.unitID ?? 0),
                      '',
                      '',
                      value
                    )
                  }
                />
              </Grid>
              <Grid item lg={3}>
                <FormDateField label="Closing Date" name="closingDate" format={DateFormat.SIMPLE_DATE} />
              </Grid>
              <Grid item lg={3}>
                <FormAutoCompleteSelectDynamic
                  name="departments"
                  label="Department"
                  getQueryKeyConfig={() => departmentsApiKeyStore.getDepartmentsWithManager()}
                  optionsValueKey="departmentID"
                  optionsLabelKey="departmentName"
                />
              </Grid>
              {isDUXEnabled && isSyncWithDuxPermission && (
                <Grid item lg={3}>
                  <FormCheckbox label="Sync With DUX" name="syncWithDux" disabled={!unit?.projectSyncWithDux} />
                </Grid>
              )}
            </Grid>{' '}
            <Grid container spacing={1} className="normalPad">
              <Grid item lg={3}>
                <FormInput label="Building Permit#" name="buildingPermit" />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Mechanical Permit#" name="mechanicalPermit" />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Electrical Permit#" name="electricalPermit" />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Plumbing Permit#" name="plumbingPermit" />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Roof Permit#" name="roofPermit" />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Irrigation Permit#" name="irrigationPermit" />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Driveway Permit#" name="drivewayPermit" />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Gas Permit#" name="gasPermit" />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Low Voltage Permit#" name="lowVoltagePermit" />
              </Grid>

              <Grid item lg={3}>
                <FormInput label="Application#" name="applicationNumber" />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Parcel ID" name="parcelID" />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Alternate ID" name="alternateID" />
              </Grid>
              <Grid item lg={3}>
                <FormDateField label="Permit Date" name="permitDate" format={DateFormat.SIMPLE_DATE} />
              </Grid>
            </Grid>{' '}
            <Grid container spacing={1} className="normalPad">
              <Grid item lg={3}>
                <FormInput label="Sq.Ft" name="squareFeet" type="number" />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Ach Area" name="achArea" type="number" />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Volume" name="volume" type="number" />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Window Area" name="windowArea" type="number" />
              </Grid>

              <Grid item lg={3}>
                <FormInput label="Registration Number" name="registrationNumber" />
              </Grid>
              <Grid item lg={3}>
                <FormInput label="Hers Score" name="hersScore" type="number" />
              </Grid>

              {/* <Grid item lg={3}>
                <FormSelect
                  label="Billing Against"
                  name="billingAgainst"
                  options={[
                    { value: 'division', label: 'Division' },
                    { value: 'corporate', label: 'Corporate Office' },
                  ]}
                />
              </Grid> */}
              <UnitRebate project={projectData?.data} />
            </Grid>
            <Grid container spacing={1} className="normalPad">
              <Grid item lg={3}>
                <FormCheckbox label="Energy Star" name="energyStar" onChange={handleEnergyStarCheckbox} />
              </Grid>
              <Grid item lg={9} sm={9} xl={9} xs={9}>
                {isEnergyStart && (
                  <Grid container spacing={2}>
                    <Grid item lg={4} sm={4} xl={4} xs={4}>
                      <FormInput label="Airflow" name="airflow" type="number" />
                    </Grid>
                    <Grid item lg={4} sm={4} xl={4} xs={4}>
                      <GuardedComponent requiredPermissions={[UserPermissionName.UPLOAD_FILE]}>
                        {!file && !UnitHVACFileName && (
                          <div className="inlineUploadwithLabel" style={styling.dropZone} {...getRootProps()}>
                            <label className="label">HVAC Design File</label>
                            <input {...getInputProps()} />
                            {isDragActive ? (
                              <p>Drop the files here ...</p>
                            ) : (
                              <div>
                                <p>Drag and drop a file here, or click to select a file</p>
                              </div>
                            )}
                          </div>
                        )}
                      </GuardedComponent>
                      {(file || UnitHVACFileName) && (
                        <div className="inlineDownloadwithLabel">
                          <div>
                            <label className="label">HVAC Design File</label>
                            <label className="fileName"> {!file ? (UnitHVACFileName ?? '') : file?.name}</label>
                          </div>
                          <div>
                            <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_FILE]}>
                              {!!UnitHVACFileName && (
                                <span>
                                  <IconButton aria-label="download" onClick={() => handleDownloadFile(unit)}>
                                    <img src={Icons.FileDownload} alt="Download" />
                                  </IconButton>
                                </span>
                              )}
                            </GuardedComponent>
                            <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_FILE]}>
                              <span>
                                <IconButton aria-label="delete" onClick={handleOpenDeleteDialog}>
                                  <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                                    <DeleteIcon className="btnDelete" />
                                  </Tooltip>
                                </IconButton>
                              </span>
                            </GuardedComponent>
                          </div>
                        </div>
                      )}
                    </Grid>
                    <Grid item lg={4} sm={4} xl={4} xs={4}>
                      <FormInput label="HVAC Contractor" name="hvacContractor" />
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid container spacing={1} className="normalPad">
              <Grid item lg={3}>
                <FormCheckbox label="HVAC Grading" name="includeHVACGrading" onChange={handleHVACGradingCheckbox} />
                {isHVACGrading && <FormCheckbox label="Weigh in data received" name="weighInDataReceived" />}
              </Grid>
              <Grid item lg={9} sm={9} xl={9} xs={9}>
                {isHVACGrading && (
                  <Grid container spacing={2}>
                    <Grid item lg={12} sm={12} xl={12} xs={12}>
                      <Grid container spacing={2}>
                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                          <FormInput name="sys1DesignedAHModel" label="System 1 Designed AH Model" />
                        </Grid>
                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                          <FormInput
                            name="sys1BlowerFanAirflowDesign"
                            label="System 1 Blower Fan Airflow Design"
                            type="number"
                          />
                        </Grid>
                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                          <FormSelect
                            name="sys1BlowerFanAirflowDesignSetting"
                            label="System 1 Blower Fan Airflow Design Setting"
                            options={options}
                          />
                        </Grid>
                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                          <FormInput name="designedAHModel" label="System 2 Designed AH Model" />
                        </Grid>
                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                          <FormInput
                            name="blowerFanAirflowDesign"
                            label="System 2 Blower Fan Airflow Design"
                            type="number"
                          />
                        </Grid>
                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                          <FormSelect
                            label="System 2 Blower Fan Airflow Design Setting"
                            name="blowerFanAirflowDesignSetting"
                            options={options}
                          />
                        </Grid>
                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                          <GuardedComponent requiredPermissions={[UserPermissionName.UPLOAD_FILE]}>
                            {!accFile && !unitAccaReportFileName && (
                              <div className="inlineUploadwithLabel" style={styling.dropZone} {...getAccRootProps()}>
                                <label className="label">ACCA 310 Design Report</label>
                                <input {...getAccInputProps()} />
                                {isAccDragActive ? (
                                  <p>Drop the files here ...</p>
                                ) : (
                                  <div>
                                    <p>Drag and drop a file here, or click to select a file</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </GuardedComponent>
                          {(accFile || unitAccaReportFileName) && (
                            <div className="inlineDownloadwithLabel">
                              <div>
                                <label className="label">ACCA 310 Design Report</label>
                                <label className="fileName">
                                  {' '}
                                  {!accFile ? (unitAccaReportFileName ?? '') : accFile?.name}
                                </label>
                              </div>
                              <div>
                                <GuardedComponent requiredPermissions={[UserPermissionName.DOWNLOAD_FILE]}>
                                  {!!unitAccaReportFileName && (
                                    <span>
                                      <IconButton aria-label="download" onClick={() => handleDownloadAccFile(unit)}>
                                        <img src={Icons.FileDownload} alt="Download" />
                                      </IconButton>
                                    </span>
                                  )}
                                </GuardedComponent>
                                <GuardedComponent requiredPermissions={[UserPermissionName.DELETE_FILE]}>
                                  <span>
                                    <IconButton aria-label="delete" onClick={handleOpenAccDeleteDialog}>
                                      <Tooltip title={<Typography variant="body1">Delete</Typography>}>
                                        <DeleteIcon className="btnDelete" />
                                      </Tooltip>
                                    </IconButton>
                                  </span>
                                </GuardedComponent>
                              </div>
                            </div>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={12} sm={12} xl={12} xs={12}>
                      <Grid container spacing={2}>
                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                          <FormInput name="sys1AirFlowResult" label="System 1 Airflow Result" type="number" disabled />
                        </Grid>
                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                          <FormInput
                            name="sys1AirFlowScoring"
                            label="System 1 Airflow Scoring"
                            type="number"
                            disabled
                          />
                        </Grid>
                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                          <FormInput name="sys1AirFlowGrading" label="System 1 Airflow Grading" disabled />
                        </Grid>
                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                          <FormInput name="sys2AirFlowResult" label="System 2 Airflow Result" type="number" disabled />
                        </Grid>
                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                          <FormInput
                            name="sys2AirFlowScoring"
                            label="System 2 Airflow Scoring"
                            type="number"
                            disabled
                          />
                        </Grid>
                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                          <FormInput name="sys2AirFlowGrading" label="System 2 Airflow Grading" disabled />
                        </Grid>
                        <Grid item lg={4} sm={4} xl={4} xs={4}>
                          <FormInput name="liqLineLength" label="Liquid Line Length" disabled />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <ContactsForm
                name="unitContacts"
                label="Contacts"
                projectID={unit.projectID}
                updateContactList={handleUnitContactList}
              />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <ProctorResultComponent unitID={Number(unit?.unitID ?? 0)} projectID={unit?.projectID} />
            </Grid>
            {/* <Grid item lg={12} sm={12} xl={12} xs={12}>
              <ServicesForm
                name="officeServices"
                showPlan={(val: boolean) => {}}
                page="office"
                id={!unit?.unitID ? 0 : unit?.unitID}
              />
            </Grid> */}
          </Grid>
        </Grid>

        <Grid container spacing={0.5} style={{ width: '100%' }}>
          <Grid
            item
            lg={12}
            sm={12}
            xl={12}
            xs={12}
            spacing={1}
            style={{
              display: 'flex',
              width: '100%',
              flex: '1',
              padding: '15px 30px 30px',
              justifyContent: 'space-between',
              borderTop: '1px solid #ccc',
            }}
          >
            <Grid
              container
              direction="row"
              sx={{
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
              }}
            >
              <GuardedComponent requiredPermissions={[UserPermissionName.VIEW_NOTE]}>
                <Button
                  type="button"
                  fullWidth
                  variant="outlined"
                  onClick={handleOpenNoteDialogClick}
                  style={styling.buttonStyle}
                >
                  NOTES
                </Button>
              </GuardedComponent>
              <div style={{ paddingLeft: 10 }}>
                <Button
                  type="button"
                  fullWidth
                  variant="outlined"
                  onClick={handleOpenFileDialogClick}
                  style={styling.buttonStyle}
                >
                  FILES
                </Button>
              </div>
            </Grid>
            <Button
              type={'button'}
              disabled={isLoading || isReadOnlyPermission}
              variant="contained"
              disableElevation={true}
              style={styling.buttonSave}
              className="btn btn-primary"
              onClick={handleFormSubmit}
            >
              Save
            </Button>
          </Grid>

          {openNoteDialog && (
            <NotesDialog
              title="UNIT"
              noteRequestArgs={{ unitID: unit.unitID }}
              open={openNoteDialog}
              onClose={handleCloseNoteDialog}
            />
          )}
          {openFileDialog && (
            <UnitDocumentDialog
              open={openFileDialog}
              documentRequestArgs={{ unitID: unit?.unitID }}
              onClose={() => setOpenFileDialog(false)}
            />
          )}
          {openDeleteDialog && (
            <KendoDialog className="modalpopup" title={'CONFIRM DELETE'} onClose={handleClosedDeleteDialog}>
              <div>
                <div style={{ padding: '25px 50px' }}>
                  <div>Are you sure you want to permanently delete?</div>
                  <LoadingIndicator isLoading={fileDeleteLoading} />
                  <ApiErrorComponent isError={isError} errorRespnse={error} />
                </div>
              </div>
              <DialogActionsBar>
                <div className=" spaceBetween">
                  <button className="btn btn-sm btn-default btn-lightGrey" onClick={handleClosedDeleteDialog} autoFocus>
                    Cancel
                  </button>
                  <button
                    className="btn btn-sm btn-danger btn-delete"
                    type="button"
                    style={{
                      backgroundColor: isReadOnlyPermission ? '#D7DBDD' : '#FF8A8A',
                      color: isReadOnlyPermission ? 'black' : '#fff',
                    }}
                    onClick={handleDeleteFileClick}
                    disabled={isLoading || isReadOnlyPermission}
                  >
                    Delete
                  </button>
                </div>
              </DialogActionsBar>
            </KendoDialog>
          )}
          {openAccDeleteDialog && (
            <KendoDialog className="modalpopup" title={'CONFIRM DELETE'} onClose={handleClosedAccDeleteDialog}>
              <div>
                <div style={{ padding: '25px 50px' }}>
                  <div>Are you sure you want to permanently delete?</div>
                  <LoadingIndicator isLoading={accReportDeleteLoading} />
                  <ApiErrorComponent isError={isError} errorRespnse={error} />
                </div>
              </div>
              <DialogActionsBar>
                <div className=" spaceBetween">
                  <button
                    className="btn btn-sm btn-default btn-lightGrey"
                    onClick={handleClosedAccDeleteDialog}
                    autoFocus
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-sm btn-danger btn-delete"
                    type="button"
                    style={{
                      backgroundColor: isReadOnlyPermission ? '#D7DBDD' : '#FF8A8A',
                      color: isReadOnlyPermission ? 'black' : '#fff',
                    }}
                    onClick={handleAccDeleteFileClick}
                    disabled={isLoading || isReadOnlyPermission}
                  >
                    Delete
                  </button>
                </div>
              </DialogActionsBar>
            </KendoDialog>
          )}
        </Grid>
      </ReactHookForm>
    </>
  );
};
function getRootProps(): import('react/jsx-runtime').JSX.IntrinsicAttributes &
  import('react').ClassAttributes<HTMLDivElement> &
  import('react').HTMLAttributes<HTMLDivElement> {
  throw new Error('Function not implemented.');
}

function getInputProps(): import('react/jsx-runtime').JSX.IntrinsicAttributes &
  import('react').ClassAttributes<HTMLInputElement> &
  import('react').InputHTMLAttributes<HTMLInputElement> {
  throw new Error('Function not implemented.');
}
// function handleDrop<T extends File>(acceptedFiles: T[], fileRejections: FileRejection[], event: DropEvent): void {
//   throw new Error('Function not implemented.');
// }
function setIsOpen(arg0: boolean) {
  throw new Error('Function not implemented.');
}
